import React from 'react';
import {
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { BoyIcon } from '../../../../common/icons/Icons';
import { FacilityIcons } from '../Accommodations';
import { OrdinaryButton } from '../../../../common/Buttons';

const EVENT_BOOKING = 1;
const EVENT_CHECKIN = 2;

/** First picture URL keyed by room id (string); loaded in RoomOffers via react-query (same keys as useRoomPicturesData). */
const RoomMainPhotoThumbnail = ({ imageUrl, title }) => (
  <Box
    sx={{
      width: { xs: 96, md: 112 },
      height: { xs: 72, md: 84 },
      flexShrink: 0,
      borderRadius: 1,
      overflow: 'hidden',
      bgcolor: 'grey.200',
      alignSelf: 'flex-start',
    }}
  >
    {imageUrl ? (
      <Box
        component="img"
        src={imageUrl}
        alt={title ? `${title} — zdjęcie` : ''}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    ) : null}
  </Box>
);

const getDaysLabel = (days) => {
  if (days === 1) return '1 dzień';
  return `${days} dni`;
};

const formatPaymentRuleWhen = (rule) => {
  const { event, daysBeforeOrAfter } = rule;
  if (event === EVENT_BOOKING) {
    if (daysBeforeOrAfter === 0) return 'w momencie rezerwacji';
    return `${getDaysLabel(daysBeforeOrAfter)} od rezerwacji`;
  }
  if (event === EVENT_CHECKIN) {
    if (daysBeforeOrAfter === 0) return 'w dniu zameldowania';
    return `${getDaysLabel(daysBeforeOrAfter)} przed zameldowaniem`;
  }
  return '';
};

const formatDate = (d) => {
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

const getPaymentDueDate = (rule, checkin) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkinDate = checkin ? new Date(checkin) : null;
  if (checkinDate) checkinDate.setHours(0, 0, 0, 0);

  const { event, daysBeforeOrAfter } = rule;

  if (event === EVENT_BOOKING) {
    const due = new Date(today);
    due.setDate(due.getDate() + (daysBeforeOrAfter ?? 0));
    if (checkinDate && due > checkinDate) return checkinDate;
    return due;
  }
  if (event === EVENT_CHECKIN && checkinDate) {
    const due = new Date(checkinDate);
    due.setDate(due.getDate() - (daysBeforeOrAfter ?? 0));
    if (due < today) return today;
    return due;
  }
  return today;
};

/**
 * Renders payment schedule: when and how much the customer will pay.
 * @param {{ paymentRules: Array, totalPrice: number, checkin: string|Date }} props
 */
const PaymentConditions = ({ paymentRules = [], totalPrice, checkin }) => {
  if (!paymentRules?.length || totalPrice == null) return null;

  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.25 }}>
        Harmonogram płatności
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 1.5, '& li': { mb: 0.25 } }}>
      {paymentRules.map((rule, i) => {
        const amount = Math.round((totalPrice || 0) * (rule.percentage ?? 0));
        const pct = Math.round((rule.percentage ?? 0) * 100);
        const when = formatPaymentRuleWhen(rule);
        const dueDate = getPaymentDueDate(rule, checkin);
        const dateStr = formatDate(dueDate);
        return (
          <Typography key={i} component="li" variant="caption" color="text.secondary">
            {amount} PLN ({pct}%) – {when} ({dateStr})
          </Typography>
        );
      })}
      </Box>
    </Box>
  );
};

/**
 * Booking.com-style table: one row per room, sub-rows per rate plan.
 * Columns: Room photo + details | Reservation terms | Total price | Book
 */
const RoomOffersTable = ({
  roomOffers = [],
  rooms = [],
  ratePlans = [],
  mergedRoomItems = [],
  roomMainPhotoByRoomId = {},
  adults = 2,
  checkin,
  checkout,
  onSelectOffer,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const getRoom = (roomId) => rooms.find((r) => r.id === roomId);
  // Match by id like mergeAccommodationsWithRooms (specs often have string id, roomOffer.roomId may be number)
  const getMergedItem = (roomId) =>
    mergedRoomItems.find((m) => String(m.id) === String(roomId) || m.id === roomId) || getRoom(roomId);
  const getPlan = (planId) => ratePlans.find((p) => p.id === planId);

  const getMainPhotoUrl = (roomId) => roomMainPhotoByRoomId[String(roomId)];

  const lengthOfStay =
    checkin && checkout
      ? Math.ceil((new Date(checkout) - new Date(checkin)) / (24 * 60 * 60 * 1000))
      : 0;

  const sortedRoomOffers = React.useMemo(() => {
    const withSortedPlans = roomOffers.map((ro) => ({
      ...ro,
      plans: [...(ro.plans || [])].sort(
        (a, b) => (a.totalPrice ?? Infinity) - (b.totalPrice ?? Infinity)
      ),
    }));
    return withSortedPlans.sort((a, b) => {
      const minA = Math.min(...(a.plans || []).map((p) => p.totalPrice ?? Infinity));
      const minB = Math.min(...(b.plans || []).map((p) => p.totalPrice ?? Infinity));
      return minA - minB;
    });
  }, [roomOffers]);

  if (!roomOffers.length) {
    return (
      <Typography color="text.secondary" sx={{ py: 3 }}>
        Brak dostępnych ofert dla wybranych dat.
      </Typography>
    );
  }

  if (isMobile) {
    return (
      <Stack spacing={3}>
        {sortedRoomOffers.map((roomOffer) => {
          const room = getRoom(roomOffer.roomId);
          const mergedItem = getMergedItem(roomOffer.roomId);
          const plans = roomOffer.plans || [];
          if (!room) return null;

          const roomTitle = mergedItem?.name ?? room.name;
          return (
            <Paper key={roomOffer.roomId} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Box
                sx={{
                  p: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  gap: 2,
                  alignItems: 'flex-start',
                }}
              >
                <RoomMainPhotoThumbnail imageUrl={getMainPhotoUrl(roomOffer.roomId)} title={roomTitle} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {roomTitle}
                  </Typography>
                  {mergedItem?.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {mergedItem.description}
                    </Typography>
                  )}
                  {Array.isArray(mergedItem?.other) && mergedItem.other.length > 0 && (
                    <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap sx={{ mt: 1 }}>
                      {mergedItem.other.map((s, i) => (
                        <Chip key={i} label={s} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  )}
                  <Box sx={{ mt: 1 }}>
                    <FacilityIcons item={mergedItem || room} />
                  </Box>
                </Box>
              </Box>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  {plans.map((planOffer) => {
                    const plan = getPlan(planOffer.planId);
                    return (
                      <Grid item xs={12} sm={6} key={planOffer.planId}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              {Array.from({ length: adults ?? 0 }).map((_, i) => {
                                const translateX = Math.min((i * -10) - 7);
                                return (
                                  <BoyIcon
                                    key={i}
                                    sx={{
                                      fontSize: 24,
                                      width: 24,
                                      height: 24,
                                      color: 'primary.main',
                                      transform: `translate(${translateX}px, 0px)`,
                                    }}
                                  />
                                );
                              })}
                            </Box>
                            {plan ? (
                              <>
                                <Typography variant="body2" fontWeight={600}>
                                  {plan.name}
                                </Typography>
                                {plan.description && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {plan.description}
                                  </Typography>
                                )}
                                <PaymentConditions
                                  paymentRules={plan.paymentRules}
                                  totalPrice={planOffer.totalPrice}
                                  checkin={checkin}
                                />
                              </>
                            ) : (
                              <Typography variant="body2">—</Typography>
                            )}
                            <Typography fontWeight={600} sx={{ mt: 1 }}>
                              {planOffer.totalPrice != null ? `${planOffer.totalPrice} PLN` : '—'}
                            </Typography>
                            {lengthOfStay > 0 && planOffer.totalPrice != null && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {Math.round(planOffer.totalPrice / lengthOfStay)} PLN / noc
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ mt: 2, mb: 1, display: 'flex', justifyContent: 'center' }}>
                            <OrdinaryButton
                              text="Rezerwuj"
                              onClick={() =>
                                onSelectOffer?.({
                                  roomId: roomOffer.roomId,
                                  planId: planOffer.planId,
                                  totalPrice: planOffer.totalPrice,
                                })
                              }
                            />
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Paper>
          );
        })}
      </Stack>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
      <Table size="medium" aria-label="Dostępne pokoje i stawki">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.100' }}>
            <TableCell sx={{ fontWeight: 700 }}>Pokój</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Warunki rezerwacji</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>
              Cena całkowita
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 700 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRoomOffers.map((roomOffer) => {
            const room = getRoom(roomOffer.roomId);
            const mergedItem = getMergedItem(roomOffer.roomId);
            const plans = roomOffer.plans || [];
            if (!room) return null;

            const roomDetailsTitle = mergedItem?.name ?? room.name;

            return plans.map((planOffer, planIndex) => {
              const plan = getPlan(planOffer.planId);
              const isFirstPlanRow = planIndex === 0;
              const rowSpan = plans.length;

              return (
                <TableRow
                  key={`${roomOffer.roomId}-${planOffer.planId}`}
                  sx={{
                    '&:hover td:nth-last-child(-n+3)': { backgroundColor: 'action.hover' },
                  }}
                >
                  {isFirstPlanRow && (
                    <TableCell rowSpan={rowSpan} sx={{ verticalAlign: 'top', maxWidth: 440 }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <RoomMainPhotoThumbnail
                          imageUrl={getMainPhotoUrl(roomOffer.roomId)}
                          title={roomDetailsTitle}
                        />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {roomDetailsTitle}
                          </Typography>
                          {mergedItem?.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {mergedItem.description}
                            </Typography>
                          )}
                          {Array.isArray(mergedItem?.other) && mergedItem.other.length > 0 && (
                            <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap sx={{ mt: 1 }}>
                              {mergedItem.other.map((s, i) => (
                                <Chip key={i} label={s} size="small" variant="outlined" />
                              ))}
                            </Stack>
                          )}
                          <Box sx={{ mt: 1 }}>
                            <FacilityIcons item={mergedItem || room} />
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                  )}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      {Array.from({ length: adults ?? 0 }).map((_, i) => {
                        const translateX = Math.min((i * -10) - 7);
                        return (
                          <BoyIcon
                            key={i}
                            sx={{
                              fontSize: 24,
                              width: 24,
                              height: 24,
                              color: 'primary.main',
                              transform: `translate(${translateX}px, 0px)`,
                            }}
                          />
                        );
                      })}
                    </Box>
                    {plan ? (
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {plan.name}
                        </Typography>
                        {plan.description && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {plan.description}
                          </Typography>
                        )}
                        <PaymentConditions
                          paymentRules={plan.paymentRules}
                          totalPrice={planOffer.totalPrice}
                          checkin={checkin}
                        />
                      </Box>
                    ) : (
                      <Typography variant="body2">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={600}>
                      {planOffer.totalPrice != null ? `${planOffer.totalPrice} PLN` : '—'}
                    </Typography>
                    {lengthOfStay > 0 && planOffer.totalPrice != null && (
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(planOffer.totalPrice / lengthOfStay)} PLN / noc
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ pb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <OrdinaryButton
                        text="Rezerwuj"
                        onClick={() =>
                          onSelectOffer?.({
                            roomId: roomOffer.roomId,
                            planId: planOffer.planId,
                            totalPrice: planOffer.totalPrice,
                          })
                        }
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              );
            });
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RoomOffersTable;
